/**
 * Home, the hub across Chat, Mail, Calendar and Docs (Slack's Home plus a Gmail / Google Calendar "today" digest):
 * what each section shows, in what order, and how much. Pure functions; HomeViewModel fetches the agenda and the
 * mail digest, the screens feed these the sidebar and the page tree. Every platform renders the same rules.
 *
 * Order (desktop and phones): Chat unreads, Today, Email, Docs, then the rest of Chat (channels, direct messages,
 * agents at work). A section with nothing to show is left out; Today stays (saying "No more events today") while
 * mail is on, because an empty afternoon is worth knowing.
 */
import type { Attendee, CalendarEvent, EventOccurrence, MailAddress, MailTabDef, MailTabId, MailTabSummary, MailThread } from "@bundle/contracts";
import { addDays, localParts, startOfDay } from "./calendar.ts";
import { displayName, mailCodeView, mailWaitTabs } from "./mail.ts";
import type { AgentRow, ChannelRow, DmRow, SidebarRow, SidebarSection } from "./sidebar.ts";

/** Upcoming events listed under Today (today and tomorrow together). */
export const HOME_EVENTS_MAX = 4;
/** Attendee faces per event (the rest is "+N"). */
export const HOME_FACES_MAX = 3;
/** Unread / needs-reply threads under Email. */
export const HOME_THREADS_MAX = 4;
/** Unread conversations under Unreads. */
export const HOME_UNREADS_MAX = 8;
/** Channels shown when nothing is unread, and recent direct messages. */
export const HOME_ROWS_MAX = 5;
/** Recently edited docs. */
export const HOME_DOCS_MAX = 5;
/** Rows per briefing card (the rest is "+N" behind the card's link). */
export const BRIEFING_ROWS_MAX = 3;
/** How far ahead Home looks for invites I haven't answered. */
export const INVITE_DAYS = 14;

export type HomeSectionKind = "unreads" | "agenda" | "mail" | "docs" | "channels" | "dms" | "agents";

/** The greeting for the hour of day (local): morning before 12:00, afternoon before 18:00, then evening. */
export function greetingPart(hour: number): "morning" | "afternoon" | "evening" {
  return hour < 5 ? "evening" : hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
}

// ---------- Today ----------

export type HomeEvent = {
  /** `<eventId>:<occurrence start>` (unique per occurrence). */
  key: string;
  eventId: string;
  title: string;
  start: number;
  end: number;
  allDay: boolean;
  day: "today" | "tomorrow";
  /** Under way right now (start ≤ now < end, timed events only). */
  live: boolean;
  /** "Join": the event has a huddle in a conversation. */
  canJoin: boolean;
  channelId?: string;
  /** The first attendees other than me (faces), and how many more there are. */
  faces: Attendee[];
  moreFaces: number;
};

export type HomeAgenda = {
  events: HomeEvent[];
  /** Nothing left today (the screen says "No more events today"). */
  noMoreToday: boolean;
};

/** Did I decline it? (Organizers never decline their own events.) */
function declined(e: CalendarEvent, meId: string): boolean {
  if (e.organizerId === meId) return false;
  return (e.myResponse ?? e.attendees.find((a) => a.userId === meId)?.response) === "declined";
}

/**
 * The next events today and tomorrow (in my time zone), from a `listEvents` response: not cancelled, not declined,
 * not over yet. Each day lists its all-day events first, then timed ones by start (ties by title). At most `limit`.
 */
export function homeAgenda(events: CalendarEvent[], occurrences: EventOccurrence[], now: number, timeZone: string, meId: string, limit: number = HOME_EVENTS_MAX): HomeAgenda {
  const today = startOfDay(now, timeZone);
  const tomorrow = addDays(today, 1, timeZone);
  const after = addDays(today, 2, timeZone);
  const byId = new Map(events.map((e) => [e.id, e]));
  const rows: (HomeEvent & { order: number })[] = [];
  for (const o of occurrences) {
    const e = byId.get(o.eventId);
    if (e === undefined || e.cancelled || declined(e, meId)) continue;
    if (o.end <= now || o.start >= after) continue;
    const allDay = e.allDay;
    // An all-day (or multi-day) event that started earlier still belongs to today.
    const shownAt = Math.max(o.start, today);
    const day = shownAt >= tomorrow ? "tomorrow" : "today";
    const others = e.attendees.filter((a) => a.userId !== meId && a.response !== "declined");
    const ev: HomeEvent & { order: number } = {
      key: `${e.id}:${o.start}`, eventId: e.id, title: e.title, start: o.start, end: o.end, allDay, day,
      live: !allDay && o.start <= now && now < o.end,
      canJoin: e.huddle && e.channelId !== undefined,
      faces: others.slice(0, HOME_FACES_MAX), moreFaces: Math.max(0, others.length - HOME_FACES_MAX),
      order: (day === "today" ? 0 : 2) + (allDay ? 0 : 1),
    };
    if (e.channelId !== undefined) ev.channelId = e.channelId;
    rows.push(ev);
  }
  rows.sort((a, b) => a.order - b.order || a.start - b.start || (a.title < b.title ? -1 : a.title > b.title ? 1 : 0) || (a.key < b.key ? -1 : 1));
  const shown = rows.slice(0, Math.max(0, limit)).map(({ order: _o, ...ev }) => {
    void _o;
    return ev;
  });
  return { events: shown, noMoreToday: !rows.some((r) => r.day === "today") };
}

/** The range Home fetches for Today: from the start of today to the end of tomorrow. */
export function agendaRange(now: number, timeZone: string): { from: number; to: number } {
  const from = startOfDay(now, timeZone);
  return { from, to: addDays(from, 2, timeZone) };
}

/** The hour of day in my zone (the greeting). */
export function localHour(now: number, timeZone: string): number {
  return localParts(now, timeZone).hour;
}

// ---------- Email ----------

export type HomeMailTab = { tab: MailTabId; unread: number };

export type HomeThread = {
  id: string;
  mailboxId: string;
  /** "Ana, Bia (3)". */
  senders: string;
  /** The first of them: the row's picture and initials (web's and the apps' mail lists show the same one). */
  sender?: MailAddress;
  subject: string;
  snippet: string;
  unread: boolean;
  needsReply: boolean;
  lastAt: number;
  /** A verification code still in time, with when it runs out: Copy on the row. */
  code?: string;
  codeExpiresAt?: number;
};

export type HomeMail = {
  /** Inbox tabs with unread mail, in the strip's order (summed across my mailboxes). */
  tabs: HomeMailTab[];
  /** The server's tabs (MailTabDef), for their names and looks. */
  tabDefs: MailTabDef[];
  /** The most recent unread or needs-reply threads, newest first. */
  threads: HomeThread[];
  /** Unread inbox threads in all tabs. */
  unread: number;
};

function senders(participants: MailAddress[], count: number): string {
  const s = participants.slice(0, 3).map(displayName).join(", ");
  return count > 1 ? `${s} (${count})` : s;
}

/**
 * The Email digest from each mailbox's inbox listing (`listMailThreads` with no tab): unread counts per tab and the
 * newest threads that are unread or need a reply, in tabs people write to (MailTabDef.waitsOnMe: Promotions and
 * Updates only count in their chips, like Gmail's Primary). A live verification code comes first from any tab, read or
 * not. At most `limit` threads.
 */
export function homeMail(inboxes: { tabs: MailTabSummary[]; threads: MailThread[]; tabDefs?: MailTabDef[] | undefined }[], limit: number = HOME_THREADS_MAX, now: number = 0): HomeMail {
  const counts = new Map<MailTabId, number>();
  for (const box of inboxes) for (const s of box.tabs) counts.set(s.tab, (counts.get(s.tab) ?? 0) + s.unread);
  const tabs = [...counts].map(([tab, unread]) => ({ tab, unread })).filter((x) => x.unread > 0);
  const tabDefs = inboxes.find((b) => b.tabDefs !== undefined)?.tabDefs ?? [];
  const waits = mailWaitTabs(tabDefs);
  const codeOf = (t: MailThread) => {
    const c = mailCodeView(t, now);
    return c?.code === null || c?.code === undefined ? null : c;
  };
  const seen = new Set<string>();
  const picked: MailThread[] = [];
  for (const box of inboxes) {
    for (const t of box.threads) {
      if (seen.has(t.id) || t.folder !== "inbox") continue;
      if (codeOf(t) === null && (!waits.includes(t.tab) || (!t.unread && t.needsReply !== true))) continue;
      seen.add(t.id);
      picked.push(t);
    }
  }
  picked.sort((a, b) => Number(codeOf(b) !== null) - Number(codeOf(a) !== null) || b.lastAt - a.lastAt || (a.id < b.id ? 1 : -1));
  const threads = picked.slice(0, Math.max(0, limit)).map((t) => {
    const code = codeOf(t);
    return {
      id: t.id, mailboxId: t.mailboxId, senders: senders(t.participants, t.messageCount), subject: t.subject, snippet: t.snippet,
      unread: t.unread, needsReply: t.needsReply === true, lastAt: t.lastAt,
      ...(t.participants[0] === undefined ? {} : { sender: t.participants[0] }),
      ...(code === null ? {} : { code: code.copyText, codeExpiresAt: code.expiresAt }),
    };
  });
  // All's count already holds the others'.
  const all = tabDefs.find((d) => d.listsAll === true);
  return { tabs, tabDefs, threads, unread: all !== undefined ? (counts.get(all.id) ?? 0) : tabs.reduce((n, x) => n + x.unread, 0) };
}

// ---------- Chat ----------

export type HomeChat = {
  /** Unread conversations (channels, DMs, agents), mentions first, then in sidebar order. */
  unreads: SidebarRow[];
  /** When nothing is unread: my first channels (starred and folders first). */
  channels: ChannelRow[];
  /** Recent direct messages not already under Unreads. */
  dms: DmRow[];
  /** Agents working, waiting for me or failing. */
  agents: AgentRow[];
};

/** Slack's Home over the sidebar's sections: unread and mentions first, then channels, DMs and busy agents. */
export function homeChat(sections: SidebarSection[]): HomeChat {
  const rowsOf = (pred: (s: SidebarSection) => boolean) => sections.filter(pred).flatMap((s) => s.rows.filter((r): r is SidebarRow => r.type !== "page"));
  const mine = rowsOf((s) => s.id === "starred" || s.folderId !== undefined);
  const all = [...mine, ...rowsOf((s) => s.id === "channels"), ...rowsOf((s) => s.id === "dms"), ...rowsOf((s) => s.id === "agents")];
  const seen = new Set<string>();
  const unique = all.filter((r) => (seen.has(r.id) ? false : (seen.add(r.id), true)));
  const unreads = unique
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => r.mentions > 0 || (r.unread && !(r.type === "channel" && r.muted)))
    .toSorted((a, b) => (b.r.mentions > 0 ? 1 : 0) - (a.r.mentions > 0 ? 1 : 0) || a.i - b.i)
    .slice(0, HOME_UNREADS_MAX)
    .map(({ r }) => r);
  const inUnreads = new Set(unreads.map((r) => r.id));
  const channels = unreads.length > 0 ? [] : unique.filter((r): r is ChannelRow => r.type === "channel").slice(0, HOME_ROWS_MAX);
  const dms = unique.filter((r): r is DmRow => r.type === "dm" && !inUnreads.has(r.id)).slice(0, HOME_ROWS_MAX);
  const agents = unique.filter((r): r is AgentRow => r.type === "agent" && !inUnreads.has(r.id) && (r.state === "working" || r.state === "waiting" || r.state === "error"));
  return { unreads, channels, dms, agents };
}

// ---------- Briefing (phones) ----------

export type HomeInvite = {
  /** `<eventId>:<occurrence start>`. */
  key: string;
  eventId: string;
  title: string;
  start: number;
  allDay: boolean;
  organizerId: string;
};

/** My answer to an event (my own events are always accepted). */
function myRsvp(e: CalendarEvent, meId: string): string | undefined {
  if (e.organizerId === meId) return "accepted";
  return e.myResponse ?? e.attendees.find((a) => a.userId === meId)?.response;
}

/**
 * Invites I haven't answered (Yes / No / Maybe), soonest first: not cancelled, not over, each event once (its next
 * occurrence). At most `limit`.
 */
export function homeInvites(events: CalendarEvent[], occurrences: EventOccurrence[], now: number, meId: string, limit: number = BRIEFING_ROWS_MAX): HomeInvite[] {
  const byId = new Map(events.map((e) => [e.id, e]));
  const next = new Map<string, EventOccurrence>();
  for (const o of occurrences) {
    const e = byId.get(o.eventId);
    if (e === undefined || e.cancelled || o.end <= now || myRsvp(e, meId) !== "needs_action") continue;
    const seen = next.get(e.id);
    if (seen === undefined || o.start < seen.start) next.set(e.id, o);
  }
  return [...next.values()]
    .map((o) => {
      const e = byId.get(o.eventId)!;
      return { key: `${e.id}:${o.start}`, eventId: e.id, title: e.title, start: o.start, allDay: e.allDay, organizerId: e.organizerId };
    })
    .sort((a, b) => a.start - b.start || (a.key < b.key ? -1 : 1))
    .slice(0, Math.max(0, limit));
}

/** The range Home fetches on phones: Today and tomorrow, plus invites up to INVITE_DAYS ahead. */
export function briefingRange(now: number, timeZone: string): { from: number; to: number } {
  const from = startOfDay(now, timeZone);
  return { from, to: addDays(from, INVITE_DAYS, timeZone) };
}

/** The one action a row offers; none means the row only opens its item. */
export type BriefingAction = "join" | "respond" | "reply" | "review" | "copy";

export type BriefingRow =
  | { kind: "event"; event: HomeEvent; action?: "join" }
  | { kind: "invite"; invite: HomeInvite; action: "respond" }
  | { kind: "conversation"; row: SidebarRow; action?: "reply" }
  | { kind: "mail"; thread: HomeThread; action?: "reply" | "copy" }
  | { kind: "approval"; agent: AgentRow; action: "review" };

export type BriefingCardKind = "calendar" | "chat" | "mail" | "work";

export type BriefingCard = {
  kind: BriefingCardKind;
  rows: BriefingRow[];
  /** Items in this card waiting on me (the card's badge), counted before the row limit. */
  needs: number;
  /** Items left out by the row limit. */
  more: number;
};

export type HomeBriefing = {
  /** Calendar (while it's loaded), then Chat, Mail and Tasks and approvals when they have rows. */
  cards: BriefingCard[];
  /** Everything waiting on me across cards (the summary line; 0 = caught up). */
  needs: number;
  /** Of `needs`, the verification codes in time (the summary says them first). */
  codes?: number;
  /** The event under way now (the summary says it's live). */
  live?: HomeEvent;
  /** The next timed event today that hasn't started. */
  next?: HomeEvent;
};

function card(kind: BriefingCardKind, needsRows: BriefingRow[], fyiRows: BriefingRow[], limit: number): BriefingCard {
  const all = [...needsRows, ...fyiRows];
  const rows = all.slice(0, Math.max(0, limit));
  return { kind, rows, needs: needsRows.length, more: all.length - rows.length };
}

/**
 * Home on phones (the briefing): one card per area, what waits on me first in each, one action per row.
 * - Calendar: today's events (live first, Join when it has a huddle), then invites to answer (Respond).
 * - Chat: mentions, DMs and agents writing to me (Reply), then unread channels.
 * - Mail: verification codes in time (Copy), threads waiting on my reply (Reply), then unread ones.
 * - Tasks and approvals: agents waiting for me (Review).
 * `agenda` / `invites` / `mail` undefined: not loaded or off (Calendar and Mail are left out).
 */
export function homeBriefing(input: { agenda?: HomeAgenda | undefined; invites?: HomeInvite[] | undefined; chat: HomeChat; mail?: HomeMail | undefined }, limit: number = BRIEFING_ROWS_MAX): HomeBriefing {
  const cards: BriefingCard[] = [];
  const today = (input.agenda?.events ?? []).filter((e) => e.day === "today");
  const ordered = [...today.filter((e) => e.live), ...today.filter((e) => !e.live)];
  if (input.agenda !== undefined) {
    const invites: BriefingRow[] = (input.invites ?? []).map((invite) => ({ kind: "invite", invite, action: "respond" }));
    const events: BriefingRow[] = ordered.map((event) => (event.live && event.canJoin ? { kind: "event", event, action: "join" } : { kind: "event", event }));
    // Events keep their place ahead of invites, but leave room for one invite when there is one.
    const eventSlots = invites.length > 0 ? Math.max(0, limit - 1) : limit;
    const shownEvents = events.slice(0, eventSlots);
    const rows = [...shownEvents, ...invites].slice(0, Math.max(0, limit));
    cards.push({ kind: "calendar", rows, needs: invites.length, more: events.length + invites.length - rows.length });
  }
  const unreads = input.chat.unreads;
  const toMe = (r: SidebarRow) => r.mentions > 0 || (r.type !== "channel" && r.unread);
  const chat = card(
    "chat",
    unreads.filter(toMe).map((row) => ({ kind: "conversation", row, action: "reply" })),
    unreads.filter((r) => !toMe(r)).map((row) => ({ kind: "conversation", row })),
    limit,
  );
  if (chat.rows.length > 0) cards.push(chat);
  if (input.mail !== undefined) {
    const threads = input.mail.threads;
    const mail = card(
      "mail",
      [
        ...threads.filter((t) => t.code !== undefined).map((thread): BriefingRow => ({ kind: "mail", thread, action: "copy" })),
        ...threads.filter((t) => t.code === undefined && t.needsReply).map((thread): BriefingRow => ({ kind: "mail", thread, action: "reply" })),
      ],
      threads.filter((t) => t.code === undefined && !t.needsReply).map((thread) => ({ kind: "mail", thread })),
      limit,
    );
    if (mail.rows.length > 0) cards.push(mail);
  }
  const work = card("work", input.chat.agents.filter((a) => a.state === "waiting").map((agent) => ({ kind: "approval", agent, action: "review" })), [], limit);
  if (work.rows.length > 0) cards.push(work);
  const out: HomeBriefing = { cards, needs: cards.reduce((n, c) => n + c.needs, 0) };
  const codes = input.mail?.threads.filter((t) => t.code !== undefined).length ?? 0;
  if (codes > 0) out.codes = codes;
  const live = ordered.find((e) => e.live);
  const next = today.find((e) => !e.live && !e.allDay);
  if (live !== undefined) out.live = live;
  if (next !== undefined) out.next = next;
  return out;
}

// ---------- Order ----------

export type HomeCounts = {
  unreads: number;
  /** undefined: mail is off or Today has not loaded (hidden). */
  agenda?: HomeAgenda;
  /** undefined: mail is off or not loaded. */
  mail?: HomeMail;
  docs: number;
  channels: number;
  dms: number;
  agents: number;
};

/** The sections to show, in order. */
export function homeSections(c: HomeCounts): HomeSectionKind[] {
  const out: HomeSectionKind[] = [];
  if (c.unreads > 0) out.push("unreads");
  if (c.agenda !== undefined) out.push("agenda");
  if (c.mail !== undefined && (c.mail.tabs.length > 0 || c.mail.threads.length > 0)) out.push("mail");
  if (c.docs > 0) out.push("docs");
  if (c.channels > 0) out.push("channels");
  if (c.dms > 0) out.push("dms");
  if (c.agents > 0) out.push("agents");
  return out;
}
